import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Card, Button, Table, Form, Alert, Spinner } from "react-bootstrap"; // Add Spinner here
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Modal } from "react-bootstrap"; // Add Modal import
import {
  faArrowLeft,
  faExclamationCircle,
  faInfoCircle,
  faQuestionCircle,
  faFilePdf
} from "@fortawesome/free-solid-svg-icons";

import HeadingBanner from "../components/HeadingBanner";
import FreeTrialBanner from "../components/FreeTrialBanner";
import LeftPanel from "../components/LeftPanel";
import OrientationPrompt from "../components/OrientationPrompt";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { saveAs } from "file-saver";

import {
  whoami,
  getClasses,
  getSchools,
  getAladdinExportOptionClasses,
  getAladdinExportResults,
  postAladdinExportAuditLog,
  sendAladdinResultsEmail,
  getTeachers,
  getTeachersBySchoolId,
  getTrialistsBySchool,
  getPilotBySchoolId,
} from "../services/APIClient";

import {
  groupClassResultsforExport
} from "../services/GroupAndAverageTasks";
import {
  formatAladdinAvailabilityDate,
  useAladdinExportAvailability,
} from "../contexts/AladdinExportAvailabilityContext";

import "../components/AladdinExport.css";

// Add these custom styles
const customStyles = `
  .staff-contact-list {
    max-height: 300px;
    overflow-y: auto;
  }
  
  .staff-contact-item {
    transition: all 0.2s ease;
    background-color: #f8f9fa;
  }
  
  .staff-contact-item:hover {
    background-color: #e9ecef;
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  }
  
  .role-icon {
    font-size: 1.5rem;
    min-width: 40px;
    text-align: center;
  }
  
  .staff-name {
    text-align: center;
    width: 100%;
  }

  .aladdin-status-banner {
    font-size: 1.1rem;
    font-weight: bold;
    margin-bottom: 20px;
    padding: 15px;
    border-radius: 8px;
    text-align: center;
  }

  .aladdin-status-open {
    background-color: #d4edda;
    border: 2px solid #28a745;
    color: #155724;
  }

  .aladdin-status-closed {
    background-color: #f8d7da;
    border: 2px solid #dc3545;
    color: #721c24;
  }

  .help-button-prominent {
    background: linear-gradient(45deg, #007bff, #0056b3);
    color: white;
    border: none;
    padding: 15px 25px;
    font-size: 1.1rem;
    font-weight: bold;
    border-radius: 8px;
    box-shadow: 0 4px 8px rgba(0,123,255,0.3);
    transition: all 0.3s ease;
    margin-bottom: 25px;
    width: 100%;
  }

  .help-button-prominent:hover {
    background: linear-gradient(45deg, #0056b3, #003d82);
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0,123,255,0.4);
  }
`;

// Define role constants for consistent role checking
const ROLES = {
  SENCO: 'SENCO',
  PRINCIPAL: 'PRINCIPAL',
  SECRETARY: 'SECRETARY',
  ALPACA_LEAD: 'ALPACA LEAD',
  SET: 'SET'
};

const isStaging =
  window.location.href.includes("staging") ||
  window.location.href.includes("localhost") ||
  window.location.href.includes("alpaca-assessment-test");

export const getAladdinAcademicYearTimeframe = (academicYear) => ({
  start: academicYear.start.toISOString(),
  end: academicYear.end.toISOString(),
});

function AladdinExport({ asOfDate }) {
  const { options: aladdinOptions } = useAladdinExportAvailability();
  const [schoolid, setSchoolid] = useState(null);
  const [schoolName, setSchoolName] = useState(null);
  const [teacherid, setTeacherid] = useState(null);
  const [teachers, setTeachers] = useState(null);
  const [rollNumber, setRollNumber] = useState(null);
  const [teacherEmail, setTeacherEmail] = useState(null);
  const [teacherName, setTeacherName] = useState(null);
  const [classes, setClasses] = useState([]);
  const [classLoadError, setClassLoadError] = useState("");
  const [screeners, setScreeners] = useState([]);
  const [selectedScreener, setSelectedScreener] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedClasses, setSelectedClasses] = useState([]); // Store selected class IDs
  const [selectAll, setSelectAll] = useState(false);
  const [assessmentResults, setAssessmentResults] = useState(null); // Store fetched results
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [showModal, setShowModal] = useState(false); // State for modal visibility
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [hasExportPermission, setHasExportPermission] = useState(true);
  const [trialistDetails, setTrialistDetails] = useState(null);
  const [freeDaysLeft, setFreeDaysLeft] = useState(null);
  const [pilotData, setPilotData] = useState(null);
  const academicYearOptions = useMemo(() => Array.from(
    new Map(
      aladdinOptions.map((option) => [
        option.academicYear,
        {
          storage: option.academicYear,
          display: option.academicYear.replace("-", "/"),
          start: new Date(option.academicYearStart),
          end: new Date(option.academicYearEnd),
        },
      ])
    ).values()
  ).sort((a, b) => b.storage.localeCompare(a.storage)), [aladdinOptions]);
  const selectedYearOption = academicYearOptions.find(
    ({ display }) => display === selectedYear
  );

  useEffect(() => {
    if (selectedYear && !selectedYearOption && academicYearOptions[0]) {
      setSelectedYear(academicYearOptions[0].display);
      setSelectedScreener(null);
    }
  }, [academicYearOptions, selectedYear, selectedYearOption]);

  useEffect(() => {
    if (!selectedYearOption) {
      setScreeners([]);
      return;
    }
    setScreeners(
      aladdinOptions
        .filter((option) => option.academicYear === selectedYearOption.storage)
        .map((option) => ({
          id: option.assessmentId,
          name: option.assessmentName,
          available: option.available,
          message: option.message,
          availableFrom: option.availableFrom,
        }))
    );
  }, [aladdinOptions, selectedYearOption]);

  const [showHelpModal, setShowHelpModal] = useState(false);
  const PDF_URL = "https://www.alpaca-assessment.com/hubfs/Aladdin%20Guides/How%20to%20Import%20ALPACA%20Results%20into%20Aladdin%20and%20Map%20Unmapped%20Students.pdf";
  const VIDEO_URL = "https://www.youtube.com/watch?v=K9vgbw9w6bI";
  const PDF_FILENAME = "Aladdin_Import_Guide_ALPACA.pdf";

  // Function to fetch the PDF and add it to the ZIP file
  const addPDFToZip = async (zip) => {
    try {
      const response = await fetch(PDF_URL);
      const pdfBlob = await response.blob();
      zip.file("Aladdin_Import_Guide_ALPACA.pdf", pdfBlob);
      return zip;
    } catch (error) {
      console.error("Error adding PDF to ZIP:", error);
      return zip;
    }
  };

  const handleScreenerSelect = (screenerId) => {
    if (!screeners.find(({ id }) => id === screenerId)?.available) return;
    setSelectedScreener(screenerId);
  };

  // Add this state variable for tracking selected teachers

  // Add this function to handle sending emails to multiple teachers
  /* 
  const handleSendToMultipleTeachers = async () => {
    if (!selectedTeachers || selectedTeachers.length === 0) {
      alert("Please select at least one teacher to email results.");
      return;
    }

    setEmailLoading(true);
    setShowModal(false);

    try {
      console.log(`Fetching results for classes:`, selectedClasses);
      let response = await getClassesAssessmentResults(selectedClasses, selectedScreener);

      if (!response || Object.keys(response).length === 0) {
        alert("No results found.");
        setEmailLoading(false);
        return;
      }

      // Process data
      response = groupClassResultsforExport(response);

      // Structure the payload
      const assessmentName = screeners.find((s) => s.id === selectedScreener)?.name || "Assessment";
      const classNames = selectedClasses.map(classId => response[classId]?.name || `Class_${classId}`);

      const emailPayload = {
        assessmentName,
        schoolName,
        rollNumber,
        selectedYear: selectedYearOption?.storage,
        selectedClasses: selectedClasses.map((classId) => ({
          classId,
          className: response[classId]?.name || `Class_${classId}`,
          students: response[classId]?.students || {},
        })),
      };

      console.log("📨 Preparing to send emails with payload:", emailPayload);

      // Track the export via API call just once for all recipients
      await postAladdinExportAuditLog(
        teacherid,
        schoolid,
        assessmentName,
        selectedScreener,
        selectedClasses,
        classNames,
        "Email",
        selectedYearOption?.storage,
      );

      console.log("📝 Export tracked successfully in audit logs!");

      // Send emails to all selected teachers sequentially
      const results = [];
      let successCount = 0;
      let failCount = 0;

      for (const teacher of selectedTeachers) {
        try {
          console.log(`Sending email to ${teacher.name} (${teacher.email})...`);
          await sendAladdinResultsEmail(teacher.email, teacher.name, emailPayload);
          results.push({ email: teacher.email, success: true });
          successCount++;
        } catch (error) {
          console.error(`Failed to send email to ${teacher.email}:`, error);
          results.push({ email: teacher.email, success: false, error: error.message });
          failCount++;
        }
      }

      // Provide a detailed alert
      if (successCount === selectedTeachers.length) {
        alert(`✅ Email sent successfully to ${successCount} teacher${successCount !== 1 ? 's' : ''}!`);
      } else if (successCount > 0) {
        alert(`📧 Partial success: Email sent to ${successCount} of ${selectedTeachers.length} teachers. ${failCount} failed.`);
      } else {
        alert(`❌ Failed to send emails to any teachers. Please try again later.`);
      }

      console.log("Email sending results:", results);
    } catch (error) {
      console.error("❌ Error in email process:", error);
      alert("Failed to process or send emails. Please try again later.");
    } finally {
      setEmailLoading(false);
      setSelectedTeachers([]);
    }
  };
  */

  useEffect(() => {
    whoami()
      .then((response) => {
        // const { schoolid, id, name, additionalRoles, countryid } =
        // whoamiResponse.data.stakeholder;

        setSchoolid(response.data.stakeholder.schoolid);
        setTeacherid(response.data.stakeholder.id);
        setTeacherEmail(response.data.email);
        setTeacherName(response.data.stakeholder.name);

        // Set user roles for permission checking
        const roles = response.data.stakeholder.additionalRoles || [];
        setUserRoles(roles);

        // Check if user has export permission based on roles
        // const hasPermission = checkUserExportPermission(roles);


        setHasExportPermission(true);

        console.log(response)
      })
      .catch((error) => console.error("Error fetching user info:", error));
  }, []);

  // Updated function to check if user has permission to export results with safety checks
  const checkUserExportPermission = (roles) => {
    // Ensure roles is always an array with safety check
    const safeRoles = Array.isArray(roles) ? roles : [];

    if (safeRoles.length === 0) return false;

    return safeRoles.some(role => {
      const roleUpper = role.toUpperCase();
      return roleUpper === ROLES.SENCO ||
        roleUpper.includes(ROLES.PRINCIPAL) ||
        roleUpper === ROLES.SECRETARY ||
        roleUpper === ROLES.ALPACA_LEAD ||
        roleUpper === ROLES.SET;
    });
  };

  useEffect(() => {
    async function fetchSchoolData() {
      if (!schoolid) return;

      try {
        setLoading(true);
        setClassLoadError("");

        // Use getTeachers() instead of getTeachersBySchoolId()
        const teachersResponse = await getTeachers();

        // Debug logging to inspect the teachers data structure
        console.log("Teachers API Response:", teachersResponse);

        // Check if teachersResponse is an array - if not, convert to empty array
        const normalizedTeachers = Array.isArray(teachersResponse) ? teachersResponse : [];
        console.log("Normalized teachers:", normalizedTeachers);

        setTeachers(normalizedTeachers);

        const classesResponse = await getClasses();
        classesResponse.sort((a, b) => new Date(b.date_created) - new Date(a.date_created));
        setClasses(classesResponse);

        const schoolsResponse = await getSchools();
        const matchingSchool = schoolsResponse.data.find((school) => school.id === schoolid);

        if (matchingSchool) {
          setRollNumber(matchingSchool.rollnumber);
          setSchoolName(matchingSchool.name);

        }
      } catch (error) {
        console.error("Error fetching school data:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchSchoolData();
  }, [schoolid]);

  useEffect(() => {
    if (!schoolid) return;
    const fetchTrialistDetails = async () => {
      try {
        const response = await getTrialistsBySchool(schoolid);
        console.log("[FreeTrialBanner debug] trialist response:", response);
        if (response.length > 0) {
          const details = response[0];
          setTrialistDetails(details);
          const daysLeft = Math.ceil(
            (new Date(details.end_date) - new Date()) / (1000 * 60 * 60 * 24)
          );
          setFreeDaysLeft(daysLeft > 0 ? daysLeft : 0);
        }
      } catch (error) {
        console.error("Failed to fetch trialist details:", error);
      }
    };
    fetchTrialistDetails();
  }, [schoolid]);

  useEffect(() => {
    if (!schoolid) return;
    const fetchPilotData = async () => {
      try {
        const pilotResponse = await getPilotBySchoolId(schoolid);
        setPilotData(pilotResponse);
      } catch (error) {
        console.error("Error fetching pilot data:", error);
      }
    };
    fetchPilotData();
  }, [schoolid]);

  console.log("teachers", teachers);

  const getIconFileName = (assessmentName) => {
    let name = assessmentName.toLowerCase();
    if (name.includes("baseline") || name.includes("autumn")) return "autumn";
    if (name.includes("midpoint") || name.includes("spring")) return "spring";
    if (name.includes("end of year") || name.includes("summer")) return "summer";
    return "default";
  };

  const handleClassSelect = (classId) => {
    setSelectedClasses((prevSelected) =>
      prevSelected.includes(classId)
        ? prevSelected.filter((id) => id !== classId)
        : [...prevSelected, classId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedClasses([]); // Unselect all
    } else {
      setSelectedClasses(classes.map((cls) => cls.classId)); // Select all
    }
    setSelectAll(!selectAll);
  };

  const generateZipFileName = () => {
    const schoolPart = schoolName ? schoolName.replace(/\s+/g, "_") : "School";
    const rollNumberPart = rollNumber ? rollNumber.replace(/\s+/g, "_") : "rollNumber";
    const assessmentPart = screeners.find((s) => s.id === selectedScreener)?.name || "Assessment";
    const formattedDate = formatDate(new Date().toISOString()); // Today's date in DD/MM/YY
    const yearPart = selectedYearOption ? `_${selectedYearOption.storage}` : "";

    return `${rollNumberPart}_${schoolPart}_${assessmentPart}${yearPart}_${formattedDate}.zip`.replace(/\s+/g, "_");
  };

  const prepareResultsZip = async () => {
    if (selectedClasses.length === 0) {
      alert("Please select at least one class.");
      return null;
    }

    try {
      console.log(`Fetching results for classes:`, selectedClasses);
      let response = await getAladdinExportResults(
        schoolid,
        selectedClasses,
        selectedScreener,
        selectedYearOption?.storage
      );

      console.log("API Response:", response);
      if (!response || Object.keys(response).length === 0) {
        alert("No results found.");
        return null;
      }

      response = groupClassResultsforExport(response);
      const zip = new JSZip();

      selectedClasses.forEach(classId => {
        const classData = response[classId];
        if (!classData || !classData.students) return;

        const className = classData.name || `Class_${classId}`;
        const assessmentName = screeners.find(s => s.id === selectedScreener)?.name || "Assessment";
        const filename = `${rollNumber}_${className}_${assessmentName}.xlsx`.replace(/\s+/g, "_");

        let flattenedResults = [];
        let uniqueTasks = new Set();

        Object.values(classData.students).forEach(student => {
          student.results.forEach(assessment => {
            assessment.groupedTasks.forEach(taskGroup => {
              uniqueTasks.add(taskGroup.name);
            });
          });
        });

        const taskColumns = [...uniqueTasks];

        Object.entries(classData.students).forEach(([studentId, student]) => {
          let rawDate = student.results[0]?.date;  // Extract raw date
          let formattedDate = rawDate ? formatDate(rawDate) : "";  // ✅ Format properly

          let totalScore = 0;
          let taskCount = 0;

          student.results.forEach(assessment => {
            assessment.groupedTasks.forEach(taskGroup => {
              if (taskGroup.aggregated.rawScore !== null) {
                totalScore += taskGroup.aggregated.rawScore;
                taskCount++;
              }
            });
          });

          let readingScore = taskCount > 0 ? Math.round(totalScore / taskCount) : "";

          let studentRow = {
            "Student First and Last Name": student.name.trim(),
            "Test Date (D/M/Y)": formattedDate,
            "Reading Score": readingScore, // ✅ Add Reading Score here
          };

          student.results.forEach(assessment => {
            assessment.groupedTasks.forEach(taskGroup => {
              studentRow[`Raw Score (${taskGroup.name})`] = taskGroup.aggregated.rawScore ?? "";
              studentRow[`Standard Score (${taskGroup.name})`] = taskGroup.aggregated.standardScore ?? "";
            });
          });

          flattenedResults.push(studentRow);
        });

        const headers = ["Student First and Last Name", "Test Date (D/M/Y)", "Reading Score", ...taskColumns.flatMap(task => [`Raw Score (${task})`, `Standard Score (${task})`])];
        const worksheet = XLSX.utils.json_to_sheet(flattenedResults, { header: headers });
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Results");

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        zip.file(filename, excelBuffer);
      });

      const zipWithGuide = await addPDFToZip(zip);
      if (Object.keys(zipWithGuide.files).length === 0) {
        alert("No valid results.");
        return null;
      }
      return zipWithGuide;
    } catch (error) {
      console.error("Error preparing ZIP:", error);
      alert(error.response?.data?.error?.message || "Error preparing ZIP file.");
      return null;
    }
  };

  const exportResults = async () => {
    if (selectedClasses.length === 0) {
      alert("Please select at least one class to export.");
      return;
    }

    setExportLoading(true);

    try {
      const zip = await prepareResultsZip();
      if (!zip) {
        setExportLoading(false);
        return;
      }

      // ✅ **Track the export via API call**
      const assessmentName = screeners.find((s) => s.id === selectedScreener)?.name || "Assessment";
      const classNames = selectedClasses.map(classId => classes.find(cls => cls.classId === classId)?.name || `Class_${classId}`);

      await postAladdinExportAuditLog(
        teacherid, // Current logged-in teacher ID
        schoolid,  // Selected school ID
        assessmentName,
        selectedScreener,
        selectedClasses, // List of class IDs
        classNames,      // List of class names
        "Export",
        selectedYearOption?.storage,
      );

      console.log("📝 Export tracked successfully in audit logs!");

      zip.generateAsync({ type: "blob" }).then(blob => {
        saveAs(blob, generateZipFileName());
        setExportLoading(false);
      });

    } catch (error) {
      console.error("❌ Error exporting results:", error);
      alert("Failed to export results.");
      setExportLoading(false);
    }
  };

  // Email function is commented out but preserved
  /* 
  const emailResults = async () => {
    if (selectedClasses.length === 0) {
      alert("Please select at least one class to email results.");
      return;
    }

    setEmailLoading(true);

    try {
      console.log(`Fetching results for classes:`, selectedClasses);
      let response = await getClassesAssessmentResults(selectedClasses, selectedScreener);

      if (!response || Object.keys(response).length === 0) {
        alert("No results found.");
        setEmailLoading(false);
        return;
      }

      // ✅ Process data in the frontend
      response = groupClassResultsforExport(response);

      // ✅ Structure the payload
      const assessmentName = screeners.find((s) => s.id === selectedScreener)?.name || "Assessment";
      const classNames = selectedClasses.map(classId => response[classId]?.name || `Class_${classId}`);

      const emailPayload = {
        assessmentName,
        schoolName,
        rollNumber,
        selectedClasses: selectedClasses.map((classId) => ({
          classId,
          className: response[classId]?.name || `Class_${classId}`,
          students: response[classId]?.students || {},
        })),
      };

      console.log("📨 Sending email request with payload:", emailPayload);

      // ✅ **Track the export via API call**
      await postAladdinExportAuditLog(
        teacherid, // Current logged-in teacher ID
        schoolid,  // Selected school ID
        assessmentName,
        selectedScreener,
        selectedClasses, // List of class IDs
        classNames,     // List of class names
        "Email",
        selectedYearOption?.storage,
      );

      console.log("📝 Export tracked successfully in audit logs!");

      // ✅ Send structured data to backend
      await sendAladdinResultsEmail(teacherEmail, teacherName, emailPayload);

      alert("✅ Email sent successfully!");
    } catch (error) {
      console.error("❌ Error sending email:", error);
      alert("Failed to send email.");
    } finally {
      setEmailLoading(false);
    }
  };
  */

  const formatDate = (isoDate) => {
    if (!isoDate) return ""; // Handle missing dates

    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return "Invalid Date"; // Handle incorrect date formats

    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const year = String(date.getUTCFullYear()).slice(-2);

    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    async function fetchClassesForScreener() {
      if (!selectedScreener || !schoolid || !selectedYear) return;

      try {
        setLoading(true);

        if (!selectedYearOption) {
          console.error("Invalid year selected:", selectedYear);
          return;
        }

        const filteredClasses = await getAladdinExportOptionClasses(
          schoolid,
          selectedYearOption.storage,
          selectedScreener
        );

        console.log("Filtered classes from API:", filteredClasses);
        setClasses(filteredClasses);
        setSelectedClasses([]); // Reset selection when year changes
        setSelectAll(false);
      } catch (error) {
        console.error("Error fetching classes for selected screener:", error);
        setClasses([]);
        setClassLoadError(
          error.response?.data?.error?.message ||
          "This Aladdin Export option is currently unavailable."
        );
      } finally {
        setLoading(false);
      }
    }

    fetchClassesForScreener();
  }, [selectedScreener, schoolid, selectedYear, selectedYearOption]);

  // Completely defensive implementation of getFilteredTeachers
  const getFilteredTeachers = () => {
    // Ensure teachers is an array
    if (!teachers || !Array.isArray(teachers)) {
      console.log("Teachers is not an array:", teachers);
      return [];
    }

    console.log("Getting filtered teachers from:", teachers);

    try {
      return teachers.filter(teacher => {
        // Check if teacher is an object
        if (!teacher || typeof teacher !== 'object') {
          console.log("Invalid teacher item:", teacher);
          return false;
        }

        // Ensure additionalRoles is an array with safety check
        const roles = Array.isArray(teacher.additionalRoles) ? teacher.additionalRoles : [];

        // If no roles, skip them
        if (roles.length === 0) {
          return false;
        }

        // Check for any roles that match our criteria using the same permission function
        return checkUserExportPermission(roles);
      });
    } catch (error) {
      console.error("Error in getFilteredTeachers:", error);
      return [];
    }
  };

  console.log("filtered classes", classes);
  console.log("teacherEmail", teacherEmail);
  console.log("teacherName", teacherName);

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTeachers([]);
  };

  console.log("selectedTeachers", selectedTeachers);

  // Completely defensive implementation of getAuthorizedTeachers
  const getAuthorizedTeachers = () => {
    // Ensure teachers is an array
    if (!teachers || !Array.isArray(teachers)) {
      console.log("Teachers is not an array:", teachers);
      return [];
    }

    try {
      return teachers.filter(teacher => {
        // Check if teacher is an object
        if (!teacher || typeof teacher !== 'object') {
          console.log("Invalid teacher item:", teacher);
          return false;
        }

        // Ensure additionalRoles is an array with safety check
        const roles = Array.isArray(teacher.additionalRoles) ? teacher.additionalRoles : [];

        // If no roles, skip them
        if (roles.length === 0) {
          return false;
        }

        return checkUserExportPermission(roles);
      });
    } catch (error) {
      console.error("Error in getAuthorizedTeachers:", error);
      return [];
    }
  };

  return (
    <Container fluid className="vh-100">
      {/* Add the custom styles */}
      <style>{customStyles}</style>

      <HeadingBanner name="Aladdin Export" isLoggedIn={true} cloud={true} teacherid={teacherid} />
      <Row className="h-100">
        <LeftPanel />

        <Col sm={{ span: 10, offset: 2 }} className="blue-background">
          <Card className="dynamic-box">
              {loading ? (
                <div className="loading-container d-flex flex-column align-items-center">
                  <Spinner animation="border" role="status" className="mb-2" />
                  <span>Loading...</span>
                </div>
              ) : !schoolid ? (
                <div className="step-message">
                  <h4>Enter School Roll Number to begin</h4>
                </div>
              ) : !selectedYear ? (
                <>
                  <div className="school-info-header mb-3">
                    <h4 className="mb-1">{schoolName}</h4>
                    <p className="text-muted">Roll Number: {rollNumber}</p>
                  </div>
                  <h5 className="step-indicator">Step 1: Select Academic Year</h5>
                  <Row className="justify-content-center mb-3">
                    {academicYearOptions.map((year) => (
                      <Col md={4} key={year.storage} className="mb-3">
                        <button
                          className={`year-selection-button w-100 py-3 shadow-sm rounded ${selectedYear === year.display ? "selected" : ""}`}
                          onClick={() => setSelectedYear(year.display)}
                        >
                          <h3>{year.display}</h3>
                          <small className="text-muted d-block mt-2">
                            {year.start.toLocaleDateString('en-IE', { month: 'short', year: 'numeric', timeZone: 'Europe/Dublin' })} -
                            {year.end.toLocaleDateString('en-IE', { month: 'short', year: 'numeric', timeZone: 'Europe/Dublin' })}
                          </small>
                        </button>
                      </Col>
                    ))}
                  </Row>
                </>
              ) : !selectedScreener ? (
                <>
                  <div className="current-selections p-2 mb-3 bg-light rounded">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h5 className="mb-1">{schoolName}</h5>
                        <p className="mb-0 text-muted">Academic Year: {selectedYear}</p>
                      </div>
                      <Button variant="secondary" className="back-button" onClick={() => setSelectedYear(null)}>
                        <FontAwesomeIcon icon={faArrowLeft} /> Change Year
                      </Button>
                    </div>
                  </div>

                  <h5 className="step-indicator">Step 2: Select an Assessment Point</h5>
                  <Row>
                    {screeners.length > 0 ? (
                      screeners.map((screener) => (
                        <Col md={4} key={screener.id} className="mb-3">
                          <button
                            className="light-blue-button assessment-button"
                            onClick={() => handleScreenerSelect(screener.id)}
                            disabled={!screener.available}
                            aria-disabled={!screener.available}
                          >
                            {screener.name} <br />
                            <img
                              src={`/assets/imgs/${getIconFileName(screener.name)}-icon.png`}
                              className="season-assessment-icon"
                              alt={screener.name}
                            />
                          </button>
                          {!screener.available && (
                            <Alert variant="info" className="mt-2 mb-0 small">
                              {screener.message || "This export is not available yet."}
                              {screener.availableFrom && (
                                <div>Scheduled for {formatAladdinAvailabilityDate(screener.availableFrom)}.</div>
                              )}
                            </Alert>
                          )}
                        </Col>
                      ))
                    ) : (
                      <Col>
                        <Alert variant="info" className="text-center">
                          <FontAwesomeIcon icon={faExclamationCircle} size="3x" className="mb-3" />
                          <h4>No Screeners Available</h4>
                          <p>No screeners have been completed for this school in the {selectedYear} academic year.</p>
                        </Alert>
                      </Col>
                    )}
                  </Row>
                </>
              ) : (
                <>
                  <div className="current-selections p-2 mb-3 bg-light rounded">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h5 className="mb-1">{schoolName}</h5>
                        <div className="d-flex text-muted">
                          <span className="mr-3">Year: {selectedYear}</span>, {" "}
                          <span>Assessment: {screeners.find((s) => s.id === selectedScreener)?.name}</span>
                        </div>
                      </div>
                      <Button variant="secondary" className="back-button" onClick={() => setSelectedScreener(null)}>
                        <FontAwesomeIcon icon={faArrowLeft} /> Change Assessment
                      </Button>
                    </div>
                  </div>

                  {/* Aladdin Files Status Banner */}
                  {/* {(() => {
                  const { isOpen, currentTime, dayOfWeek } = getAladdinFilesStatus();
                  return (
                    <div className={`aladdin-status-banner ${isOpen ? 'aladdin-status-open' : 'aladdin-status-closed'}`}>
                      <FontAwesomeIcon 
                        icon={isOpen ? faCircleCheck : faClock} 
                        className="me-2" 
                      />
                      {isOpen 
                        ? "🟢 Aladdin Files Open" 
                        : "🔴 Aladdin Files Open After 1:30 PM (Weekdays Only)"
                      }
                      <div className="small mt-1">
                        Current time (Ireland): {currentTime} - {dayOfWeek}
                      </div>
                    </div>
                  );
                })()} */}

                  <h5 className="step-indicator">Step 3: Select Classes for Export</h5>
                  <p>Select the class(es) you would like to export Aladdin Results for.</p>

                  {/* How to Import Results - Prominent Button */}
                  <Button
                    className="help-button-prominent"
                    onClick={() => setShowHelpModal(true)}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="me-2" />
                    📚 How to Import Results into Aladdin
                  </Button>

                  <div className="class-selection-container">
                    {/* Class Table */}
                    <Card className="class-table-card">
                      {loading ? (
                        <div className="d-flex justify-content-center align-items-center p-5">
                          <Spinner animation="border" role="status" className="mr-2" />
                          <span>Loading classes...</span>
                        </div>
                      ) : classLoadError ? (
                        <Alert variant="warning" className="text-center my-4">
                          <FontAwesomeIcon icon={faExclamationCircle} size="3x" className="mb-3" />
                          <h4>Export unavailable</h4>
                          <p>{classLoadError}</p>
                        </Alert>
                      ) : classes.length > 0 ? (
                        <>
                          <Table bordered responsive>
                            <thead>
                              <tr>
                                <th>Class Name</th>
                                <th>
                                  Select All
                                  <Form.Check
                                    className="ml-2"
                                    type="checkbox"
                                    checked={selectAll}
                                    onChange={handleSelectAll}
                                  />
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {classes.map((cls) => (
                                <tr key={cls.classId}>
                                  <td>{cls.name}</td>
                                  <td>
                                    <Form.Check
                                      type="checkbox"
                                      checked={selectedClasses.includes(cls.classId)}
                                      onChange={() => handleClassSelect(cls.classId)}
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                          {selectedClasses.length > 0 && (
                            <div className="selection-summary p-2 bg-light">
                              <p className="mb-0">Selected {selectedClasses.length} of {classes.length} classes</p>
                            </div>
                          )}
                        </>
                      ) : (
                        <Alert variant="info" className="text-center my-4">
                          <FontAwesomeIcon icon={faExclamationCircle} size="3x" className="mb-3" />
                          <h4>No Classes Found</h4>
                          <p>There are no classes available for {screeners.find((s) => s.id === selectedScreener)?.name} in the {selectedYear} academic year.</p>
                          <div className="mt-3">
                            <Button variant="outline-primary" className="mr-2" onClick={() => setSelectedYear(null)}>
                              Try Another Year
                            </Button>
                            <Button variant="outline-secondary" onClick={() => setSelectedScreener(null)}>
                              Try Another Assessment
                            </Button>
                          </div>
                        </Alert>
                      )}
                    </Card>

                    {classes.length > 0 && (
                      <div className="export-button-container mt-3">
                        {(() => {
                          if (hasExportPermission) {
                            return (
                              <Button
                                className="export-button"
                                variant="success"
                                onClick={exportResults}
                                disabled={exportLoading || selectedClasses.length === 0}
                              >
                                {exportLoading ? (
                                  <>
                                    <Spinner animation="border" size="sm" className="mr-2" />
                                    <span>Exporting...</span>
                                  </>
                                ) : "EXPORT"}
                              </Button>
                            );
                          } else {
                            return (
                              <Card className="border-info mb-0">
                                <Card.Header className="bg-info text-white">
                                  <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                  <strong>Aladdin Exporters</strong>
                                </Card.Header>
                                <Card.Body>
                                  <p>The following people have permission to download Aladdin results in the school:</p>

                                  {(() => {
                                    try {
                                      // Get authorized teachers with defensive error handling
                                      const authorizedTeachers = getAuthorizedTeachers();
                                      console.log("Authorized teachers:", authorizedTeachers);

                                      if (Array.isArray(authorizedTeachers) && authorizedTeachers.length > 0) {
                                        return (
                                          <div className="staff-contact-list">
                                            {authorizedTeachers.map((teacher, index) => {
                                              try {
                                                // Ensure additionalRoles is always an array
                                                const safeRoles = Array.isArray(teacher.additionalRoles) ? teacher.additionalRoles : [];

                                                // Get primary permission role with safety check
                                                let permissionRole = "Staff"; // Default
                                                try {
                                                  permissionRole = safeRoles.find(role => {
                                                    if (typeof role !== 'string') return false;

                                                    const roleUpper = role.toUpperCase();
                                                    return roleUpper === 'SENCO' ||
                                                      roleUpper.includes('PRINCIPAL') ||
                                                      roleUpper === 'SECRETARY' ||
                                                      roleUpper === 'ALPACA LEAD';
                                                  }) || "Staff";
                                                } catch (error) {
                                                  console.error("Error finding permission role:", error);
                                                }

                                                // Get icon for the role
                                                let roleIcon = "👤"; // Default
                                                if (permissionRole && typeof permissionRole === 'string') {
                                                  const roleUpper = permissionRole.toUpperCase();
                                                  if (roleUpper === "SENCO") roleIcon = "📊";
                                                  else if (roleUpper.includes("PRINCIPAL")) roleIcon = "🏫";
                                                  else if (roleUpper === "SECRETARY") roleIcon = "📝";
                                                  else if (roleUpper === "ALPACA LEAD") roleIcon = "🦙";
                                                }

                                                // Get other roles with safety check
                                                let otherRoles = [];
                                                try {
                                                  otherRoles = permissionRole !== "Staff"
                                                    ? safeRoles.filter(r => r !== permissionRole)
                                                    : safeRoles;
                                                } catch (error) {
                                                  console.error("Error filtering other roles:", error);
                                                }

                                                return (
                                                  <div key={index} className="staff-contact-item d-flex align-items-center p-2 mb-2 border rounded">
                                                    <div className="role-icon me-3 fs-4">
                                                      {roleIcon}
                                                    </div>
                                                    <div className="flex-grow-1">
                                                      <div className="fw-bold staff-name">{teacher.name || "Unknown Teacher"}</div>
                                                      <div className="text-muted small">
                                                        <span className="badge bg-info text-white me-2">{permissionRole}</span>
                                                        {otherRoles.length > 0 && (
                                                          <span className="small">
                                                            Also: {otherRoles.join(', ')}
                                                          </span>
                                                        )}
                                                      </div>
                                                    </div>
                                                  </div>
                                                );
                                              } catch (itemError) {
                                                console.error("Error rendering teacher item:", itemError);
                                                return null; // Skip this item if error
                                              }
                                            })}
                                          </div>
                                        );
                                      } else {
                                        return (
                                          <Alert variant="secondary">
                                            <div className="d-flex align-items-center">
                                              <FontAwesomeIcon icon={faExclamationCircle} className="me-3 fs-4" />
                                              <div>
                                                Please contact a staff member with SENCO, Principal, Secretary, or Alpaca Lead role.
                                              </div>
                                            </div>
                                          </Alert>
                                        );
                                      }
                                    } catch (error) {
                                      console.error("Error rendering authorized teachers:", error);
                                      return (
                                        <Alert variant="danger">
                                          <div className="d-flex align-items-center">
                                            <FontAwesomeIcon icon={faExclamationCircle} className="me-3 fs-4" />
                                            <div>
                                              An error occurred while loading staff information. Please refresh the page and try again.
                                            </div>
                                          </div>
                                        </Alert>
                                      );
                                    }
                                  })()}
                                </Card.Body>
                              </Card>
                            );
                          }
                        })()}

                        {/* Email button commented out but preserved for future use */}
                        {/*
                      <Button
                        className="email-button"
                        variant="success"
                        onClick={() => setShowModal(true)}
                        disabled={emailLoading || selectedClasses.length === 0}
                      >
                        {emailLoading ? (
                          <>
                            <Spinner animation="border" size="sm" className="mr-2" />
                            <span>Sending...</span>
                          </>
                        ) : "EMAIL"}
                      </Button>
                      */}
                      </div>
                    )}
                  </div>
                </>
              )}
          </Card>
        </Col>
      </Row>
      {window.innerWidth < window.innerHeight && <OrientationPrompt />}

      {trialistDetails && (
        <FreeTrialBanner
          schoolid={schoolid}
          teacherName={teacherName}
          freeDaysLeft={freeDaysLeft}
          trialistDetails={trialistDetails}
          pilotData={pilotData}
          autoOpen={true}
        />
      )}

      {/* Modal for selecting teacher to email - commented out but preserved for future use */}
      {/*
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>Select Teachers to Email</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {teachers ? (
            <div>
              <p className="text-muted mb-3">Select one or more teachers to receive the assessment results</p>
              {getFilteredTeachers().length > 0 ? (
                <Form>
                  {getFilteredTeachers().map((teacher) => (
                    <div key={teacher.id} className="teacher-selection-item mb-3 p-3 border rounded">
                      <Form.Check
                        type="checkbox"
                        id={`teacher-${teacher.id}`}
                        label={
                          <div className="d-flex flex-column">
                            <span className="font-weight-bold">{teacher.name}</span>
                            {teacher.additionalRoles && teacher.additionalRoles.length > 0 && (
                              <div className="mt-1">
                                <small className="text-muted">Roles: </small>
                                <small>{teacher.additionalRoles.join(", ")}</small>
                              </div>
                            )}
                          </div>
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            // Add teacher to selected list
                            setSelectedTeachers(prev => [...prev, teacher]);
                          } else {
                            // Remove teacher from selected list
                            setSelectedTeachers(prev => prev.filter(t => t.id !== teacher.id));
                          }
                        }}
                      />
                    </div>
                  ))}
                </Form>
              ) : (
                <Alert variant="info" className="text-center">
                  <FontAwesomeIcon icon={faExclamationCircle} className="mb-2" size="2x" />
                  <p className="mb-0">No teachers with appropriate roles found.</p>
                  <p className="small text-muted">Only SENCO, Principal, or Secretary roles can receive these results.</p>
                </Alert>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <Spinner animation="border" role="status" className="mb-2" />
              <p>Loading teachers...</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <div>
              {selectedTeachers && selectedTeachers.length > 0 && (
                <small className="text-muted">
                  {selectedTeachers.length} teacher{selectedTeachers.length !== 1 ? 's' : ''} selected
                </small>
              )}
            </div>
            <div>
              <Button variant="secondary" onClick={handleCloseModal} className="mr-2">
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSendToMultipleTeachers}
                disabled={emailLoading || !selectedTeachers || selectedTeachers.length === 0}
              >
                {emailLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="mr-2" />
                    <span>Sending...</span>
                  </>
                ) : "Send Email"}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
      */}

      {/* Help Modal */}
      <Modal
        show={showHelpModal}
        onHide={() => setShowHelpModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>How to Import ALPACA Results into Aladdin</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-4">
            <div className="embed-responsive embed-responsive-16by9" style={{ maxWidth: "100%", height: "30vh" }}>
              <iframe
                className="embed-responsive-item"
                src={`https://www.youtube.com/embed/K9vgbw9w6bI`}
                title="ALPACA to Aladdin Import Tutorial"
                allowFullScreen
                style={{ width: "60%", height: "100%" }}
              ></iframe>
            </div>
          </div>

          <div className="text-center">
            <h5 className="mb-3">Download Step-by-Step Guide</h5>
            <Button
              variant="primary"
              href={PDF_URL}
              target="_blank"
              download
            >
              <FontAwesomeIcon icon={faFilePdf} className="mr-2" />
              Download PDF Guide
            </Button>
            <p className="text-muted mt-3">
              <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
              The PDF guide will also be included in your exported ZIP file.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHelpModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
}

export default AladdinExport;
